// Streams API
import { getAPI, token } from "@/api/params.js";
import { CHANNEL_TYPE } from "@/constants.js";

/**
 * Send add stream request and return created stream ID
 * @param {Object} data Stream data
 */
export const addStream = async data => {
  /** convert data to backend server format */
  const streamData = composeStreamData(data);
  /** API URL */
  const addStreamUrl = `${getAPI()}/ndvr/stream/add?token=${token}`;

  /** API request */
  const resp = await fetch(addStreamUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(streamData)
  });

  const respObj = await resp.json();

  /** error handling */
  if (!respObj.success) {
    throw new Error(respObj.error);
  }

  return respObj.data;
};

/**
 * Change stream URL parameter
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @param {String} url new URL
 */
export const editStream = async (app, stream, url, new_display_name) => {
  const streamData = {
    app,
    url,
    stream_short_name: stream,
    new_display_name
  };

  /** API URL */
  const editStreamUrl = `${getAPI()}/ndvr/stream/edit`;

  /** API request */
  const resp = await fetch(editStreamUrl, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(streamData)
  });

  const respObj = await resp.json();

  /** error handling */
  if (!respObj.success) {
    throw new Error(respObj.error);
  }
};

/**
 * Send remove stream request
 * @param {Number} id Stream ID
 */
export const removeStream = async id => {
  /** API URL */
  const removeStreamUrl = `${getAPI()}/ndvr/stream/delete/${id}?token=${token}`;

  /** API request */
  const resp = await fetch(removeStreamUrl, {
    method: "DELETE"
  });

  const respObj = await resp.json();

  /** error handling */
  if (!respObj.success) {
    throw new Error(respObj.error);
  }
};

export const getByChannel = async channel => {
  const breaksUrl = `${getAPI()}/ndvr/stream/get_by_channel/?token=${token}&channelId=${channel}`;
  const resp = await fetch(breaksUrl);
  if (!resp.ok) {
    let error = "";
    try {
      error = await resp.json().error;
    } catch {
      error = resp.statusText;
    }
    throw new Error(error);
  }

  const { success, data, error } = await resp.json();

  if (!success) {
    throw new Error(error);
  } else {
    return data;
  }
};

/**
 * Get stream breaks array
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @returns array of break intervals
 */
export const getStreamBreaks = async (app, stream) => {
  const breaksUrl = `${getAPI()}/ndvr/stream/statistics/?token=${token}&stream=${stream}&app=${app}`;
  const resp = await fetch(breaksUrl);

  if (!resp.ok) {
    let error = "";
    try {
      error = await resp.json().error;
    } catch {
      error = resp.statusText;
    }
    throw new Error(error);
  }

  const { success, data, error } = await resp.json();

  if (!success) {
    throw new Error(error);
  }

  const breaks = parseStreamStatistics(data);
  return breaks;
};

/**
 * Get stream NDVR information (start, end, etc.)
 * @param {String} app Application name
 * @param {String} stream Stream name
 * @returns {Object} Stream data
 */
export const getStreamNdvr = async (app, stream) => {
  const ndvrUrl = `${getAPI()}/ndvr/status?${
    app ? "app=" + app : ""
  }&stream=${stream}&token=${token}`;
  const ndvrStatusResponse = await fetch(ndvrUrl);
  const { data, success, error } = await ndvrStatusResponse.json();

  if (!success) {
    throw new Error(error);
  }

  return parseNdvrData(data);
};

/**
 * Convert stream information to frontend application format
 * @param {Object} data Stream information received from backend
 * @param {Object} serverData Server data
 */
export const parseStreamData = (data, serverData) => {
  return {
    id: data.id,
    channelName: data.channel_id ? data.channel_name : null, // set 'null' if channel parameters not set
    channelType: data.only_audio ? CHANNEL_TYPE.AUDIO : CHANNEL_TYPE.VIDEO,
    streamName: data.stream,
    lastRecorderStatus: data.last_recorder_status,
    lastSourceStatus: data.last_source_status,
    streamUrl: data.input_url,
    /** data for manual adding to Mangomolo */
    streamHost: `${serverData.worker_api_protocol}://${serverData.worker_host}:${serverData.stream_port}`,
    ndvrApi: getAPI(),
    ndvrNginx: serverData.worker_storage_https_url,
    online: data.online,
    statusTimestamp: data.status_timestamp,
    streamDisplayName: data.stream_display_name,
    videoCount: data.videos_count
  };
};

/**
 * Convert stream data from front-end format to API back-end format
 * @param {Object} data Stream data
 */
function composeStreamData(data) {
  const serverData = {
    app: data.appName,

    stream_short_name: data.streamName,
    input_url: data.streamUrl,
    online: data.online,
    only_audio: data.channelType == CHANNEL_TYPE.AUDIO,
    worker_id: data.serverId.toString()
  };
  return serverData;
}

/**
 * Parse server's stream publish statuses to break intervals
 * @param {Array} statistics Stream publish statistics
 * @returns Array of break intervals
 */
function parseStreamStatistics(statistics) {
  const breaks = [];
  let currentInterval = {};

  for (const status of statistics) {
    const { status_timestamp: timestamp, publish_status: publish } = status;

    const time = Number(timestamp) / 1000;
    if (publish) {
      currentInterval.end = time;
      breaks.push(currentInterval);
      currentInterval = {};
    } else {
      currentInterval.start = time;
    }
  }

  /** last break not ended now */
  if ("start" in currentInterval) {
    breaks.push(currentInterval);
  }

  return breaks;
}

/**
 * Parse server's stream NDVR information to client format
 * @param {Object} data Server format NDVR data
 * @returns Client format ndvr data
 */
function parseNdvrData(data) {
  return {
    start: data.firstEntryTime,
    end: data.lastLiveEntryTime,
    recordedEnd: data.lastRecordedEntryTime
  };
}
